import { Radio, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

interface ReviewOptionProps {
  /** Radio group name, shared by every option of one step. */
  name: string;
  label: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  /** Detail rows rendered under the label. */
  children?: ReactNode;
  'data-testid'?: string;
}

/**
 * A selectable card with a radio. The whole card selects on click; the native
 * radio stays the keyboard and screen-reader control.
 */
export const ReviewOption = ({
  name,
  label,
  selected,
  disabled,
  onSelect,
  children,
  'data-testid': dataTestId,
}: ReviewOptionProps) => (
  <Stack
    spacing={1}
    onClick={disabled ? undefined : onSelect}
    data-testid={dataTestId}
    sx={{
      padding: 3,
      borderRadius: `${borderRadius.sm}px`,
      border: (theme) =>
        `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
      backgroundColor: 'background.paper',
      cursor: disabled ? 'default' : 'pointer',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={2}>
      <Radio
        size="small"
        name={name}
        checked={selected}
        onChange={onSelect}
        // The card's own click handler selects; don't let the radio's click bubble into a second select.
        onClick={(event) => event.stopPropagation()}
        disabled={disabled}
        inputProps={{ 'aria-label': label }}
        sx={{ padding: 0 }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
        {label}
      </Typography>
    </Stack>
    {children}
  </Stack>
);
